<template>
  <helmi-group-dashboard-dialog
    :loading="processingData"
    :show="showSummary"
    max-width="640"
    @close="showSummary = false"
    @input="resetState"
  >
    <v-toolbar
      color="primary"
      dark
      flat
      prominent
    >
      <v-list-item class="mt-4 pa-0">
        <v-list-item-avatar size="72">
          <v-img :src="agent.avatar"/>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title v-text="agent.name"/>
          <v-list-item-subtitle>
            {{ agent.company }}, {{ agent.office }}
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            <v-icon color="white" small>
              phone
            </v-icon>
            {{ agent.phone }}
            <v-icon color="white" small>
              mail
            </v-icon>
            {{ agent.email }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <template v-slot:extension>
        <v-tabs
          v-model="tab"
          background-color="primary"
          color="white"
          show-arrows
        >
          <v-tab>Yhteenveto</v-tab>
          <v-tab>Esittely</v-tab>
          <v-tab>Sopimukset</v-tab>
        </v-tabs>
      </template>
    </v-toolbar>

    <v-tabs-items v-model="tab">
      <v-tab-item class="pa-4 primary darken-1">
        <v-row align="stretch">
          <v-col cols="12" sm="6">
            <v-card color="primary" dark>
              <v-card-title>
                Keskim. välitysprosentti
              </v-card-title>

              <v-card-text>
                <v-progress-linear
                  :value="averageMediationPercentage.agentRelative"
                  color="white"
                  height="32"
                >
                  <strong class="black--text">
                    {{ averageMediationPercentage.agent }}%
                  </strong>
                </v-progress-linear>
                Konsernin keskiarvo
                <v-progress-linear
                  :value="averageMediationPercentage.groupRelative"
                  height="16"
                  color="pink"
                >
                  <strong>{{ averageMediationPercentage.group }}%</strong>
                </v-progress-linear>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            sm="6"
          >
            <v-card color="purple lighten-2" dark>
              <v-card-title>
                Provisioprosentti
              </v-card-title>

              <v-card-text>
                <v-progress-linear
                  :value="provisionPercentage.agentRelative"
                  color="white"
                  height="32"
                >
                  <span class="black--text subtitle-2">{{ provisionPercentage.agent }}%</span>
                </v-progress-linear>

                <p class="ma-0 pt-4 pb-2">
                  Konsernin keskiarvo
                </p>
                <v-progress-linear
                  :value="provisionPercentage.groupRelative"
                  height="24"
                  color="purple darken-1"
                >
                  <span class="subtitle-2">{{ provisionPercentage.group }}%</span>
                </v-progress-linear>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            v-if="summary && summary.netIncomePosition"
            cols="12"
            sm="6"
          >
            <v-card
              color="orange"
              dark
              style="overflow: hidden;"
            >
              <v-card-title>
                Nettotuottosijoitus
              </v-card-title>

              <v-row no-gutters>
                <v-col>
                  <v-card-text class="text-center orange darken-1">
                    <p class="display-3">
                      {{ summary.netIncomePosition.group }}.
                    </p>
                    Konserni
                  </v-card-text>
                </v-col>
                <v-col>
                  <v-card-text class="text-center orange lighten-1 black--text">
                    <p class="display-3">
                      {{ summary.netIncomePosition.office }}.
                    </p>
                    Toimipiste
                  </v-card-text>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6">
            <v-card color="primary darken-2" dark>
              <v-card-title>
                Nettotuottosijoitus
              </v-card-title>

              <v-card-text v-if="summary && summary.netIncomePosition">
                <p class="display-3">
                  {{ summary.netIncomePosition.office }}.
                </p>
                Toimipiste
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item>
        <v-card flat>
          <v-card-title class="headline">
            {{ agent.name }}
          </v-card-title>
          <v-card-text>
            {{ overview.data }}
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card flat>
          <v-card-title class="headline">
            An even better title
          </v-card-title>
          <v-card-text>
            <p>
              Maecenas ullamcorper, dui et placerat feugiat, eros pede varius nisi.
            </p>

            <p class="mb-0">
              Etiam vitae tortor. Curabitur ullamcorper ultricies nisi. Sed magna purus.
            </p>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <template slot="actions">
      <v-btn
        color="primary"
        text
        @click="showSummary = false"
      >
        Sulje
      </v-btn>
    </template>
  </helmi-group-dashboard-dialog>
</template>

<script>
import { EventBus } from '@/utils/eventBus';

const getInitialState = () => ({
  agent: {},
  contracts: [],
  overview: {},
  showSummary: false,
  processingData: true,
  summary: {},
  tab: 0,
});

export default {
  name: 'GroupDashboardAgentSummary',

  components: {
    'helmi-group-dashboard-dialog': () => import('@/components/groupDashboard/GroupDashboardDialog'),
  },

  data: () => getInitialState(),

  computed: {
    averageMediationPercentage() {
      if (!this.summary || !this.summary.averageMediationPercentage) {
        return {
          agent: 0,
          group: 0,
        };
      }

      return this.addRelativePercentages(this.summary.averageMediationPercentage);
    },

    provisionPercentage() {
      if (!this.summary || !this.summary.provisionPercentage) {
        return {
          agent: 0,
          group: 0,
        };
      }

      return this.addRelativePercentages(this.summary.provisionPercentage);
    },
  },

  mounted() {
    EventBus.$on('SHOW_SUMMARY', async ({ agent, promises }) => {
      this.resetState();
      this.showSummary = true;

      const [summary, overview, contracts] = await Promise.all(promises);

      this.agent = agent;
      this.contracts = contracts;
      this.overview = overview;
      this.summary = summary;
      this.processingData = false;
    });
  },

  methods: {
    addRelativePercentages(obj) {
      const [max, min] = Object.keys(obj).reduce((a, b) => (obj[a] > obj[b] ? [a, b] : [b, a]));

      return {
        ...obj,
        [`${max}Relative`]: 100,
        [`${min}Relative`]: obj[min] / obj[max] * 100,
      };
    },

    resetState() {
      console.log('RESET');
      const data = getInitialState();
      Object.keys(data).forEach((key) => {
        this[key] = data[key];
      });
    },
  },
};
</script>
